/* ============================================================
   ORNAMENTS — hand-authored Odisha heritage motifs as SVG.
   Nothing here is a stock asset. Each is drawn from geometry so
   it scales crisply and can be re-tinted via currentColor / tokens.

   Exposes window.Ornaments = { konarkWheel, pattachitraBorder, kalingaSpire, lotus }
============================================================= */
(function () {
  const NS = 'http://www.w3.org/2000/svg';
  const TAU = Math.PI * 2;
  const pol = (cx, cy, r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];

  /* ---- KONARK CHARIOT WHEEL --------------------------------
     The Sun Temple wheel: beaded outer rim, 8 broad spokes,
     8 fine spokes between them, carved axle pins, floral hub. */
  function konarkWheel(opts = {}) {
    const stroke = opts.stroke || 'url(#goldLeaf)';
    const sw = opts.width != null ? opts.width : 1.4;
    const C = 100;
    let body = '';

    // concentric rim bands
    body += `<circle cx="${C}" cy="${C}" r="96" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`;
    body += `<circle cx="${C}" cy="${C}" r="88" fill="none" stroke="${stroke}" stroke-width="${sw * 0.7}"/>`;
    body += `<circle cx="${C}" cy="${C}" r="78" fill="none" stroke="${stroke}" stroke-width="${sw * 0.7}"/>`;

    // beaded ring between r=88 and r=96
    const beads = 32;
    for (let i = 0; i < beads; i++) {
      const a = (i / beads) * TAU;
      const [x, y] = pol(C, C, 92, a);
      body += `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="1.7" fill="${stroke}"/>`;
    }

    // 8 broad spokes — tapered, with a carved teardrop tip
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      const [ix, iy] = pol(C, C, 24, a);          // inner
      const [ox, oy] = pol(C, C, 78, a);          // outer
      const [l1x, l1y] = pol(C, C, 24, a - 0.045);
      const [l2x, l2y] = pol(C, C, 78, a - 0.018);
      const [r1x, r1y] = pol(C, C, 24, a + 0.045);
      const [r2x, r2y] = pol(C, C, 78, a + 0.018);
      body += `<path d="M${l1x.toFixed(1)} ${l1y.toFixed(1)} L${l2x.toFixed(1)} ${l2y.toFixed(1)} L${r2x.toFixed(1)} ${r2y.toFixed(1)} L${r1x.toFixed(1)} ${r1y.toFixed(1)} Z" fill="none" stroke="${stroke}" stroke-width="${sw * 0.8}"/>`;
      // carved medallion near outer end of each broad spoke
      const [mx, my] = pol(C, C, 66, a);
      body += `<circle cx="${mx.toFixed(1)}" cy="${my.toFixed(1)}" r="3.4" fill="none" stroke="${stroke}" stroke-width="${sw * 0.6}"/>`;
    }

    // 8 fine spokes between broad ones
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU + TAU / 16;
      const [ix, iy] = pol(C, C, 26, a);
      const [ox, oy] = pol(C, C, 78, a);
      body += `<line x1="${ix.toFixed(1)}" y1="${iy.toFixed(1)}" x2="${ox.toFixed(1)}" y2="${oy.toFixed(1)}" stroke="${stroke}" stroke-width="${sw * 0.5}"/>`;
    }

    // axle hub — floral rosette
    body += `<circle cx="${C}" cy="${C}" r="22" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`;
    const petals = 8;
    for (let i = 0; i < petals; i++) {
      const a = (i / petals) * TAU;
      const [tx, ty] = pol(C, C, 18, a);
      const [bx, by] = pol(C, C, 6, a + TAU / petals / 2);
      const [bx2, by2] = pol(C, C, 6, a - TAU / petals / 2);
      body += `<path d="M${C} ${C} Q${bx.toFixed(1)} ${by.toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)} Q${bx2.toFixed(1)} ${by2.toFixed(1)} ${C} ${C} Z" fill="none" stroke="${stroke}" stroke-width="${sw * 0.55}"/>`;
    }
    body += `<circle cx="${C}" cy="${C}" r="4" fill="${stroke}"/>`;

    return `<svg class="orn orn--wheel" viewBox="0 0 200 200" xmlns="${NS}" aria-hidden="true">${body}</svg>`;
  }

  /* ---- LOTUS (small accent / card seal) -------------------- */
  function lotus(opts = {}) {
    const stroke = opts.stroke || 'url(#goldLeaf)';
    const C = 50;
    let body = '';
    for (let i = 0; i < 9; i++) {
      const a = -Math.PI / 2 + (i - 4) * 0.32;
      const [tx, ty] = pol(C, C + 6, 34, a);
      const cx = C + Math.cos(a) * 10;
      body += `<path d="M${C} ${C + 8} Q${(cx - 8).toFixed(1)} ${(C - 6).toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)} Q${(cx + 8).toFixed(1)} ${(C - 6).toFixed(1)} ${C} ${C + 8} Z" fill="none" stroke="${stroke}" stroke-width="1"/>`;
    }
    return `<svg class="orn orn--lotus" viewBox="0 0 100 100" xmlns="${NS}" aria-hidden="true">${body}</svg>`;
  }

  /* ---- PATTACHITRA BORDER — repeating vine + lotus strip ---- */
  function pattachitraBorder(opts = {}) {
    const stroke = opts.stroke || 'url(#goldLeaf)';
    // one tile, tiled horizontally via SVG pattern
    const tile = 80, h = 34;
    let unit = '';
    // running vine
    unit += `<path d="M0 ${h / 2} C ${tile * 0.18} ${h * 0.1}, ${tile * 0.32} ${h * 0.9}, ${tile * 0.5} ${h / 2} S ${tile * 0.82} ${h * 0.1}, ${tile} ${h / 2}" fill="none" stroke="${stroke}" stroke-width="1.2"/>`;
    // alternating lotus buds
    unit += `<g transform="translate(${tile * 0.25} ${h * 0.5})">`;
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + (i - 2) * 0.4;
      const [tx, ty] = pol(0, 0, 9, a);
      unit += `<path d="M0 2 Q${(tx * 0.4).toFixed(1)} ${(ty * 0.4 - 1).toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)} Q${(tx * 0.4).toFixed(1)} ${(ty * 0.4 + 1).toFixed(1)} 0 2 Z" fill="none" stroke="${stroke}" stroke-width="0.8"/>`;
    }
    unit += `</g>`;
    // dot accents
    unit += `<circle cx="${tile * 0.75}" cy="${h * 0.5}" r="2.4" fill="none" stroke="${stroke}" stroke-width="0.8"/>`;
    unit += `<circle cx="${tile * 0.75}" cy="${h * 0.5}" r="0.9" fill="${stroke}"/>`;

    return `<svg class="orn orn--border" viewBox="0 0 ${tile} ${h}" preserveAspectRatio="xMidYMid meet" xmlns="${NS}" aria-hidden="true">
      <defs><pattern id="pat-${(Math.abs(hashStr(stroke)) % 9999)}" width="${tile}" height="${h}" patternUnits="userSpaceOnUse">${unit}</pattern></defs>
      <rect width="${tile}" height="${h}" fill="url(#pat-${(Math.abs(hashStr(stroke)) % 9999)})"/>
    </svg>`;
  }

  /* ---- KALINGA TEMPLE SPIRE (rekha deula) silhouette -------- */
  function kalingaSpire(opts = {}) {
    const stroke = opts.stroke || 'url(#goldLeaf)';
    const fill = opts.fill || 'none';
    // curvilinear tower characteristic of Odisha temples
    const body = `
      <path d="M50 4
               C 47 4 46 6 46 9
               C 40 12 36 22 35 40
               C 30 60 24 92 18 150
               L 82 150
               C 76 92 70 60 65 40
               C 64 22 60 12 54 9
               C 54 6 53 4 50 4 Z"
            fill="${fill}" stroke="${stroke}" stroke-width="1.3"/>
      <!-- amalaka (ribbed crown) -->
      <ellipse cx="50" cy="9" rx="9" ry="3.4" fill="none" stroke="${stroke}" stroke-width="1.1"/>
      <ellipse cx="50" cy="9" rx="5" ry="2" fill="none" stroke="${stroke}" stroke-width="0.8"/>
      <line x1="50" y1="2" x2="50" y2="6" stroke="${stroke}" stroke-width="1.1"/>
      <circle cx="50" cy="2" r="1.4" fill="${stroke}"/>
      <!-- vertical rib lines (pagas) -->
      <path d="M50 16 C 49 60 49 110 50 150" fill="none" stroke="${stroke}" stroke-width="0.6" opacity="0.7"/>
      <path d="M40 40 C 36 80 32 120 28 150" fill="none" stroke="${stroke}" stroke-width="0.5" opacity="0.55"/>
      <path d="M60 40 C 64 80 68 120 72 150" fill="none" stroke="${stroke}" stroke-width="0.5" opacity="0.55"/>
      <!-- horizontal tiers (bhumi) -->
      ${[58, 78, 100, 124].map((y, i) => {
        const w = 14 + i * 9;
        return `<line x1="${50 - w}" y1="${y}" x2="${50 + w}" y2="${y}" stroke="${stroke}" stroke-width="0.5" opacity="0.5"/>`;
      }).join('')}
    `;
    return `<svg class="orn orn--spire" viewBox="0 0 100 154" preserveAspectRatio="xMidYMax meet" xmlns="${NS}" aria-hidden="true">${body}</svg>`;
  }

  function hashStr(s) { let h = 0; for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; } return h; }

  /* ---- INCENSE STICKS — 3 agarbattis fanning out of a corner ----
     Bamboo tail (light) → masala-coated body (dark) → glowing ember tip.
     A placeholder until real product photography drops into the slot. */
  function incenseSticks() {
    const vbW = 240, vbH = 460;
    const baseX = 196, baseY = vbH + 6;        // base cluster low-right, slightly off-frame
    const w = 3.0;
    const sticks = [
      { off: -4, deg: -10, len: 332 },
      { off: 4,  deg: -19, len: 372 },
      { off: 13, deg: -28, len: 318 },
    ];
    let g = '';
    sticks.forEach((s) => {
      const bamboo = s.len * 0.44;
      g += `<g transform="translate(${baseX + s.off} ${baseY}) rotate(${s.deg})">
        <rect x="${-w / 2}" y="${-bamboo}" width="${w}" height="${bamboo + 4}" rx="${w / 2}" fill="#a8814d"/>
        <rect x="${-w / 2}" y="${-s.len}" width="${w}" height="${s.len - bamboo}" rx="${w / 2}" fill="#4a2d18"/>
        <rect x="${-w / 2}" y="${-s.len}" width="${w * 0.4}" height="${s.len}" rx="${w * 0.2}" fill="rgba(255,233,196,0.14)"/>
        <circle cx="0" cy="${-s.len - 1}" r="8" fill="#ff7e34" filter="url(#emberGlow)" opacity="0.8"/>
        <circle cx="0" cy="${-s.len + 4}" r="${w * 0.95}" fill="#8d867c"/>
        <circle cx="0" cy="${-s.len}" r="${w * 0.85}" fill="url(#emberCore)"/>
      </g>`;
    });
    return `<svg class="orn orn--sticks" viewBox="0 0 ${vbW} ${vbH}" preserveAspectRatio="xMaxYMax meet" xmlns="${NS}" aria-hidden="true">
      <defs>
        <radialGradient id="emberCore" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stop-color="#fff1cf"/><stop offset="0.4" stop-color="#ffb152"/>
          <stop offset="0.74" stop-color="#ef6320"/><stop offset="1" stop-color="#b8330c"/>
        </radialGradient>
        <filter id="emberGlow" x="-300%" y="-300%" width="700%" height="700%"><feGaussianBlur stdDeviation="3.4"/></filter>
      </defs>
      ${g}
    </svg>`;
  }

  /* ---- KONARK SUN RING — radiating rays + medallion + petal ring.
     Sits concentric AROUND the chariot wheel to enrich the backdrop. */
  function templeRing(opts = {}) {
    const stroke = opts.stroke || 'url(#goldLeaf)';
    const C = 220; let b = '';
    b += `<circle cx="${C}" cy="${C}" r="210" fill="none" stroke="${stroke}" stroke-width="0.8"/>`;
    b += `<circle cx="${C}" cy="${C}" r="176" fill="none" stroke="${stroke}" stroke-width="0.6"/>`;
    // radiating sun rays (Konark = Sun Temple), alternating lengths
    const rays = 72;
    for (let i = 0; i < rays; i++) {
      const a = (i / rays) * TAU, r0 = 178, r1 = i % 2 ? 197 : 208, wd = 0.010;
      const [xa, ya] = pol(C, C, r0, a - wd), [xb, yb] = pol(C, C, r0, a + wd), [x1, y1] = pol(C, C, r1, a);
      b += `<path d="M${xa.toFixed(1)} ${ya.toFixed(1)} L${x1.toFixed(1)} ${y1.toFixed(1)} L${xb.toFixed(1)} ${yb.toFixed(1)} Z" fill="${stroke}"/>`;
    }
    // medallion beads
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * TAU, [x, y] = pol(C, C, 176, a);
      b += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.3" fill="none" stroke="${stroke}" stroke-width="0.7"/>`;
    }
    // outward-pointing lotus petals
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU, [bx, by] = pol(C, C, 158, a), [tx, ty] = pol(C, C, 173, a), pp = a + Math.PI / 2, sp = 6;
      const lx = bx + Math.cos(pp) * sp, ly = by + Math.sin(pp) * sp, rx = bx - Math.cos(pp) * sp, ry = by - Math.sin(pp) * sp;
      b += `<path d="M${lx.toFixed(1)} ${ly.toFixed(1)} Q${tx.toFixed(1)} ${ty.toFixed(1)} ${rx.toFixed(1)} ${ry.toFixed(1)}" fill="none" stroke="${stroke}" stroke-width="0.7"/>`;
    }
    return `<svg class="orn orn--ring" viewBox="0 0 440 440" xmlns="${NS}" aria-hidden="true">${b}</svg>`;
  }

  /* ---- KALINGA TEMPLE SKYLINE — a row of rekha-deula spires of
     varying height for the deep background horizon. */
  function templeSkyline(opts = {}) {
    const S = opts.stroke || 'url(#goldLeaf)', F = opts.fill || 'none';
    const base = 176, vbW = 760, vbH = 200;
    const items = [{ x: 78, s: 0.55 }, { x: 190, s: 0.86 }, { x: 332, s: 1.18 }, { x: 472, s: 0.96 }, { x: 600, s: 0.7 }, { x: 690, s: 0.5 }];
    let b = '';
    items.forEach((it) => {
      const sw = (0.9 / it.s).toFixed(2);
      const deula =
        `<path d="M50 4 C47 4 46 6 46 9 C40 12 36 22 35 40 C30 60 24 92 18 150 L82 150 C76 92 70 60 65 40 C64 22 60 12 54 9 C54 6 53 4 50 4 Z" fill="${F}" stroke="${S}" stroke-width="${sw}"/>` +
        `<ellipse cx="50" cy="9" rx="8" ry="3" fill="none" stroke="${S}" stroke-width="${sw}"/>` +
        `<line x1="50" y1="1" x2="50" y2="6" stroke="${S}" stroke-width="${sw}"/>`;
      b += `<g transform="translate(${it.x} ${base}) scale(${it.s}) translate(-50 -150)">${deula}</g>`;
    });
    return `<svg class="orn orn--skyline" viewBox="0 0 ${vbW} ${vbH}" preserveAspectRatio="xMidYMax meet" xmlns="${NS}" aria-hidden="true">${b}</svg>`;
  }

  /* ---- KONARK CARVED PILLAR — tall hollow temple column (outline
     only, like the wheel) to frame the empty hero side-margins. */
  function konarkPillar(opts = {}) {
    const S = opts.stroke || 'url(#goldLeaf)';
    const W = 120, H = 640, cx = 60, sw = 1;
    let b = '';
    const rect = (y, hh, width) => `<rect x="${cx - width / 2}" y="${y}" width="${width}" height="${hh}" fill="none" stroke="${S}" stroke-width="${sw}"/>`;

    // base / plinth
    b += rect(602, 38, 104);
    b += rect(586, 16, 84);
    b += rect(570, 16, 68);
    b += `<ellipse cx="${cx}" cy="568" rx="30" ry="5" fill="none" stroke="${S}" stroke-width="${sw}"/>`;

    // shaft
    const shTop = 122, shBot = 560, shW = 44, lx = cx - shW / 2, rx = cx + shW / 2;
    b += `<line x1="${lx}" y1="${shTop}" x2="${lx}" y2="${shBot}" stroke="${S}" stroke-width="${sw}"/>`;
    b += `<line x1="${rx}" y1="${shTop}" x2="${rx}" y2="${shBot}" stroke="${S}" stroke-width="${sw}"/>`;
    [-12, -4, 4, 12].forEach((o) => { b += `<line x1="${cx + o}" y1="${shTop + 14}" x2="${cx + o}" y2="${shBot - 14}" stroke="${S}" stroke-width="0.5" opacity="0.6"/>`; });
    // carved bands + medallions
    [206, 334, 462].forEach((y) => {
      b += `<line x1="${lx - 4}" y1="${y}" x2="${rx + 4}" y2="${y}" stroke="${S}" stroke-width="${sw}"/>`;
      b += `<line x1="${lx - 4}" y1="${y + 11}" x2="${rx + 4}" y2="${y + 11}" stroke="${S}" stroke-width="${sw}"/>`;
      b += `<circle cx="${cx}" cy="${y + 5.5}" r="4" fill="none" stroke="${S}" stroke-width="0.7"/>`;
    });

    // capital
    b += rect(98, 16, 56);
    b += rect(80, 18, 82);
    b += rect(62, 18, 104);
    // amalaka + finial
    b += `<ellipse cx="${cx}" cy="54" rx="22" ry="7" fill="none" stroke="${S}" stroke-width="${sw}"/>`;
    b += `<path d="M${cx - 12} 48 Q${cx} 26 ${cx + 12} 48" fill="none" stroke="${S}" stroke-width="${sw}"/>`;
    b += `<line x1="${cx}" y1="26" x2="${cx}" y2="16" stroke="${S}" stroke-width="${sw}"/>`;
    b += `<circle cx="${cx}" cy="14" r="2.6" fill="${S}"/>`;

    return `<svg class="orn orn--pillar" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMax meet" xmlns="${NS}" aria-hidden="true">${b}</svg>`;
  }

  window.Ornaments = { konarkWheel, pattachitraBorder, kalingaSpire, lotus, incenseSticks, templeRing, templeSkyline, konarkPillar };
})();
